<template>
  <div class="selectContainer" :class="[selectContainerClass, { 'pointer-events-none': disabled }]" ref="container">
    <div v-if="label" :class="['label', labelClass, { '!text-[#b7b2b5]': disabled }]">
      {{ label }}
    </div>

    <div :id="input_id" class="relative">
      <div class="inputCon" :class="[
        customClass,
        { disabled: disabled, 'border-active': openOption },
        warningMessage ? '!border-[#EB5554]' : '',
      ]" @click="onTriggerClick">
        <input v-if="searchable" ref="inlineInput" v-model="searchText" type="text" :placeholder="placeholder"
          class="inputText truncate outline-none bg-transparent border-none w-full" :class="isFilter
            ? 'placeholder:text-[#1F1D1F]'
            : 'placeholder:text-[#B7B2B5]'
            " :disabled="disabled" @focus="openDropdown" @click.stop="openDropdown" @keydown.stop="onKeydown" />

        <div v-else class="inputText truncate" :class="hasValueOrZero || isFilter ? 'text-[#1F1D1F]' : 'text-[#B7B2B5]'
          ">
          {{ (hasValueOrZero && nameValue) || placeholder }}
        </div>

        <div>
          <button v-if="showClearButton" type="button" class="absolute right-[10px] top-1/2 -translate-y-1/2 rounded"
            @click.stop="clearSelection">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M17.6924 7.79254C18.1025 7.38247 18.1025 6.71762 17.6924 6.30755C17.2824 5.89748 16.6175 5.89748 16.2075 6.30755L12 10.515L7.79255 6.30755C7.38248 5.89748 6.71763 5.89748 6.30756 6.30755C5.8975 6.71762 5.8975 7.38247 6.30756 7.79254L10.515 12L6.30755 16.2075C5.89748 16.6175 5.89748 17.2824 6.30755 17.6924C6.71762 18.1025 7.38247 18.1025 7.79254 17.6924L12 13.485L16.2075 17.6924C16.6175 18.1025 17.2824 18.1025 17.6925 17.6924C18.1025 17.2824 18.1025 16.6175 17.6925 16.2075L13.485 12L17.6924 7.79254Z"
                fill="#555055" />
            </svg>
          </button>

          <div v-else class="absolute right-[10px] top-1/2 -translate-y-1/2 duration-200" :class="[
            { 'rotate-180': openOption },
            disabled ? '!text-[#DFDCDD]' : '!text-[#555055]',
          ]">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M18.0905 8.0495C18.5165 8.47553 18.5165 9.16625 18.0905 9.59228L12.2723 15.4105C11.8463 15.8365 11.1555 15.8365 10.7295 15.4105L4.91132 9.59228C4.48529 9.16625 4.48529 8.47553 4.91132 8.0495C5.33734 7.62347 6.02807 7.62347 6.4541 8.0495L11.5009 13.0963L16.5477 8.0495C16.9737 7.62347 17.6644 7.62347 18.0905 8.0495Z"
                fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>
    </div>
    <div v-if="warningMessage" class="textError">
      {{ Array.isArray(warningMessage) ? warningMessage[0] : warningMessage }}
    </div>
    <teleport to="body">
      <div v-show="openOption" class="dropdownCon" :style="dropdownPosition" ref="dropdown" @click.stop>
        <template v-if="filteredOptions.length > 0 && !isNothingToShow">
          <div v-for="(item, index) in filteredOptions" :key="optionKey(item, index)" class="box" :class="{
            hidden:
              item?.hidden ||
              (!optionView && item.optionView === false) ||
              hideValues.includes(valueFromItem(item)),
            selected: isSelected(item),
          }" @mouseenter="activeIndex = index" @click.stop="selectOption(item)">
            {{ labelFromItem(item) }}
          </div>
        </template>

        <div v-else-if="isNothingToShow" class="box !text-[#B7B2B5] cursor-default" @click.stop="closeDropdown">
          ไม่พบข้อมูล
        </div>
        <div v-else class="box !text-[#B7B2B5] cursor-default" @click.stop="closeDropdown">
          ไม่พบข้อมูล
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from "vue";
import provinceData from "@/assets/json/provinces.json";
import districtData from "@/assets/json/districts.json";
import subDistrictData from "@/assets/json/subdistricts.json";
import { useAuthStore } from "@/pinia/authStore";
import { storeToRefs } from "pinia";

const auth = useAuthStore();
const { user_province_id, user_province_name } = storeToRefs(auth);

const props = defineProps({
  modelValue: { type: [String, Number, Object], default: undefined },
  value: { type: [String, Number, Object], default: "" },
  options: { type: Array, default: () => [] },
  keyValue: { type: String, default: "" },
  keyName: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  inputId: { type: String, default: "select" },
  searchable: { type: Boolean, default: false },
  isFilter: { type: Boolean, default: false },
  allowEmpty: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  warningMessage: { type: String, default: "" },
  selectContainerClass: String,
  label: String,
  labelClass: String,
  customClass: { type: String, default: "" },
  lengthOptionList: { type: Number, default: 5 },
  optionView: { type: Boolean, default: true },
  hideValues: { type: Array, default: () => [] },
  setProvince: { type: Boolean, default: false },
  provinceCode: { type: [String, Number], default: undefined },
  districtCode: { type: [String, Number], default: undefined },
  allData: { type: Boolean, default: false },
  selfSelected: { type: Boolean, default: false },
  caseInsensitive: { type: Boolean, default: true },
  emptyValue: {
  type: [String, Number, null],
  default: "",
},
});

const emit = defineEmits(["update:modelValue"]);

// --- 1. Basic State ---
const input_id = `select_${props.inputId}`;
const openOption = ref(false);
const dropdownPosition = ref({});
const dropdown = ref(null);
const container = ref(null);
const isMounted = ref(false);
const valInput = ref(
  props.modelValue !== undefined ? props.modelValue : props.value
);
const searchText = ref("");
const inlineInput = ref(null);
const activeIndex = ref(-1);

// --- 2. Helper Functions (ต้องประกาศก่อน Computed ที่จะเรียกใช้) ---
function valueFromItem(item) {
  return props.keyValue ? item?.[props.keyValue] : item;
}

function labelFromItem(item) {
  if (props.keyName) return item?.[props.keyName] ?? "";
  return typeof item === "string" ? item : item?.label ?? "";
}

// --- 3. Computed Properties ---
const provinceDataList = computed(() => {
  return [
    {
      id: "0",
      provinceCode: "",
      provinceNameTh: "แสดงทุกจังหวัด",
    },
    ...provinceData,
  ];
});

const districtDataList = computed(() => {
  const provinceCode = props.setProvince
    ? props.provinceCode
    : user_province_id;

  const districts = districtData.filter(
    (district) => `${district.provinceCode}` === `${provinceCode}`
  );

  return [
    {
      id: "0",
      districtCode: "",
      districtNameTh: "แสดงทุกอำเภอ",
    },
    ...districts,
  ];
});

const subDistrictDataList = computed(() => {
  let subdistricts = subDistrictData.filter(
    (subdistrict) => subdistrict.districtCode == props.districtCode
  );
  return [
    {
      id: "0",
      subdistrictCode: "",
      subdistrictNameTh: "แสดงทุกตำบล",
    },
    ...subdistricts,
  ];
});

const optionsList = computed(() => {
  if (props.inputId?.includes("provinceSelector")) return provinceData || [];

  if (props.inputId?.includes("filterProvinceSelector"))
    return provinceDataList.value || [];

  if (props.inputId?.includes("districtSelector")) {
    return (districtData || []).filter(
      (d) => `${d.provinceCode}` === `${props.provinceCode}`
    );
  }

  if (props.inputId?.includes("filterDistrictSelector")) {
    return districtDataList.value || [];
  }

  if (props.inputId?.includes("subDistrictSelector")) {
    return (subDistrictData || []).filter(
      (s) => `${s.districtCode}` === `${props.districtCode}`
    );
  }

  if (props.inputId?.includes("filterSubDistrictSelector")) {
    return subDistrictDataList.value || [];
  }

  return props.options || [];
});

const nameValue = computed(() => {
  const v = valInput.value;
if (v === null || v === undefined) return "";
  const found = optionsList.value.find((el) =>
    props.keyValue
      ? `${el?.[props.keyValue]}` == `${v}`
      : JSON.stringify(el) === JSON.stringify(v)
  );
  return labelFromItem(found) || "";
});

const hasValueOrZero = computed(
  () =>
    valInput.value !== "" &&
    valInput.value !== null &&
    valInput.value !== undefined
);

const filteredOptions = computed(() => {
  if (
    !props.searchable ||
    !searchText.value.trim() ||
    searchText.value === nameValue.value
  )
    return optionsList.value;
  const norm = (s) =>
    props.caseInsensitive ? String(s ?? "").toLowerCase() : String(s ?? "");
  const nq = norm(searchText.value);
  return optionsList.value.filter((opt) =>
    norm(labelFromItem(opt)).includes(nq)
  );
});

const isNothingToShow = computed(() => {
  if (filteredOptions.value.length === 0) return true;
  return filteredOptions.value.every((item) => {
    return (
      item?.hidden === true || props.hideValues.includes(valueFromItem(item))
    );
  });
});

// const showClearButton = computed(
//   () => props.allowEmpty && !props.disabled && hasValueOrZero.value
// );
const showClearButton = computed(() => {
   if (!props.allowEmpty) return false;

   return (
    valInput.value !== null &&
    valInput.value !== undefined &&
    valInput.value !== props.emptyValue
  );
});

// --- 4. Watchers (วางไว้หลัง Computed เพราะเรียกใช้ nameValue) ---
watch(
  () => [props.modelValue, props.value],
  () => {
    const incoming =
      props.modelValue !== undefined ? props.modelValue : props.value;
    valInput.value = incoming;
    if (props.searchable) {
      if ( incoming === null || incoming === undefined)
        searchText.value = "";
      else if (!openOption.value) searchText.value = nameValue.value;
    }
  },
  { immediate: true }
);

watch(valInput, (newVal) => {
  openOption.value = false;
  if (!isMounted.value) return;

  const currentPropValue =
    props.modelValue !== undefined ? props.modelValue : props.value;
  if (newVal === currentPropValue && !props.selfSelected) return;

  if (props.allData) {
    const fullObject = props.keyValue
      ? optionsList.value.find(
        (item) => `${item[props.keyValue]}` === `${newVal}`
      )
      : newVal;
    emit("update:modelValue", fullObject || null);
  } else {
    emit("update:modelValue", newVal);
  }
});

// --- 5. Methods ---
function emitCloseOthers() {
  window.dispatchEvent(
    new CustomEvent("close-all-selectbox", { detail: input_id })
  );
}

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

function updateDropdownPosition() {
  const triggerEl = document.getElementById(input_id);
  if (!triggerEl) return;

  const rect = triggerEl.getBoundingClientRect();

  const viewportHeight = window.visualViewport
    ? window.visualViewport.height
    : window.innerHeight;

  const viewportOffsetTop = window.visualViewport
    ? window.visualViewport.offsetTop
    : 0;

  const dropdownMaxHeight = props.lengthOptionList * 40 + 16;

  const shouldShowAbove =
    viewportHeight - rect.bottom < dropdownMaxHeight &&
    rect.top > dropdownMaxHeight;

  dropdownPosition.value = {
    position: "fixed",
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    maxHeight: `${dropdownMaxHeight}px`,
    zIndex: 9999,
    background: "#fff",
    border: "1px solid #b7b2b5",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    top: shouldShowAbove ? "auto" : `${rect.bottom + viewportOffsetTop + 4}px`,
    bottom: shouldShowAbove ? `${viewportHeight - rect.top + 4}px` : "auto",
  };
}

function openDropdown() {
  if (props.disabled) return;

  emitCloseOthers();
  openOption.value = true;

  nextTick(() => {
    updateDropdownPosition();

    if (props.searchable) {
      inlineInput.value?.focus();

      if (isIOS) {
        inlineInput.value?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }

    activeIndex.value = filteredOptions.value.length ? 0 : -1;
  });
}

function onTriggerClick() {
  if (props.disabled) return;
  if (!openOption.value) {
    openDropdown();
    inlineInput.value?.focus();
  } else {
    closeDropdown();
  }
}

function closeDropdown() {
  openOption.value = false;
  activeIndex.value = -1;
}

function onKeydown(e) {
  const len = filteredOptions.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    if (!openOption.value) openDropdown();
    if (len > 0) activeIndex.value = (activeIndex.value + 1) % len;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    if (!openOption.value) openDropdown();
    if (len > 0) activeIndex.value = (activeIndex.value - 1 + len) % len;
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (activeIndex.value !== -1)
      selectOption(filteredOptions.value[activeIndex.value]);
  } else if (e.key === "Escape") {
    closeDropdown();
  }
}

function selectOption(item) {
  if (props.isFilter && props.searchable && item.id == 0) {
    closeDropdown();
    return;
  }

  valInput.value = valueFromItem(item);
  if (props.searchable) searchText.value = labelFromItem(item);
  closeDropdown();
}

function isSelected(item) {
  return `${valueFromItem(item)}` === `${valInput.value}`;
}

function optionKey(item, idx) {
  if (typeof item === "object") {
    if (props.keyValue && item[props.keyValue] !== undefined)
      return `k-${item[props.keyValue]}`;
    if ("id" in item) return `id-${item.id}`;
  }
  return `i-${idx}`;
}

function clearSelection() {
  valInput.value = null;
  searchText.value = "";
  // openDropdown();
}

function onClickOutside(e) {
  if (
    container.value &&
    !container.value.contains(e.target) &&
    dropdown.value &&
    !dropdown.value.contains(e.target)
  ) {
    closeDropdown();
  }
}

onMounted(async () => {
  document.addEventListener("click", onClickOutside);

  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", updateDropdownPosition);
    window.visualViewport.addEventListener("scroll", updateDropdownPosition);
  }

  window.addEventListener(
    "close-all-selectbox",
    (e) => e.detail !== input_id && closeDropdown()
  );

  nextTick(() => (isMounted.value = true));
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside);

  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);

  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", updateDropdownPosition);
    window.visualViewport.removeEventListener("scroll", updateDropdownPosition);
  }
});
</script>

<style scoped lang="scss">
.selectContainer {
  width: 100%;
  min-width: 140px;
  display: flex;
  flex-direction: column;

  .label {
    color: #1f1d1f;
    /* fields/title */
    font-family: Kanit;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    /* 150% */
    letter-spacing: 0.1px;
    margin-bottom: 8px;
  }

  .inputCon {
    position: relative;
    display: flex;
    max-height: 42px;
    padding: 10px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    border: 1px solid #b7b2b5;
    background: #fff;
    cursor: pointer;

    .inputText {
      width: calc(100% - 24px);

      font-family: Kanit;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 21px;
      letter-spacing: 0.1px;
    }

    &.disabled {
      background: #f3f2f2;
      cursor: not-allowed;
      border-color: #dfdcdd;
    }

    &.border-active {
      border-color: #b7b2b5;
    }
  }
}
</style>

<style lang="scss">
.dropdownCon {
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  z-index: 9999;
  padding: 4px;

  .box {
    cursor: pointer;
    padding: 10px;
    border-radius: 4px;
    color: #1f1d1f;

    font-family: Kanit;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0.1px;

    &:hover,
    &.selected,
    &.active-index {
      background: #fbeff8;
    }

    &.hidden {
      display: none;
    }
  }
}
</style>